export interface Citation {
  id: number;
  title: string;
  url: string;
  snippet?: string;
}

export type IntentType =
  | 'email_query'
  | 'calendar_query'
  | 'scheduling_query'
  | 'document_query'
  | 'web_search'
  | 'general_knowledge';

export type ActiveContextMode =
  | 'document'
  | 'email'
  | 'calendar'
  | 'scheduling'
  | 'web_search'
  | 'general';

export interface ActiveContext {
  mode: ActiveContextMode;
  lastTask?: string;
  optionsPresented?: string[];
  selectedOptionHint?: string;
  documentNames?: string[];
  lastAssistantGroundingSource?: string;
}

export interface DocumentChunk {
  id: string;
  chunkIndex: number;
  totalChunks: number;
  content: string;
}

export interface DocumentContextChunk {
  fileName: string;
  content: string;
  chunkIndex: number;
  totalChunks: number;
  relevanceScore?: number;
  selectionMode?: 'excerpt' | 'full_document';
}

export interface Message {
  id: string;
  content: string;
  sender: 'user' | 'ai';
  timestamp: Date;
  isUser?: boolean; // Keep for backward compatibility
  citations?: Citation[];
  groundingSource?: string;
  threadSummaries?: ThreadSummaryEntry[];
  suggestedApp?: SuggestedApp | null;
  /** The turn ended on an unrecoverable 401; the UI shows a "Sign in again" button. Not persisted. */
  requiresSignIn?: boolean;
}

/**
 * An internal app that owns this question better than the chat assistant does.
 *
 * Populated only for apps the user's AD groups allow. Because messages persist
 * to localStorage and are rehydrated later, a stored suggestion can outlive the
 * access that produced it -- so the renderer re-checks group membership before
 * showing anything. See config/appRegistry.ts (canAccessApp).
 */
export interface SuggestedApp {
  key: string;
  name: string;
  url: string;
  summary?: string;
}

export interface ThreadSummaryEntry {
  messageId: string;
  sentAt: string;
  sender: string;
  subject?: string;
  summary: string;
}

export interface Document {
  id: string;
  fileName: string;
  originalFileName: string;
  contentType: string;
  fileSize: number;
  uploadDate: Date;
  userId: string;
  storagePath?: string;
  extractedText?: string;
  conversationId?: string;
  sessionDocument?: boolean;
  status?: 'processing' | 'ready' | 'failed';
  kind?: 'spreadsheet' | 'narrative' | 'mixed' | 'unknown' | 'unsupported';
  expiresAt?: string;
  summary?: {
    kind?: string;
    sheetCount?: number;
    tableCount?: number;
    rowCount?: number;
    chunkCount?: number;
    warnings?: string[];
  };
}

export interface ChatResponse {
  id?: string;
  content: string;
  message: string; // Alias for content for backward compatibility
  timestamp?: string | Date;
  sessionId?: string;
  isStreaming?: boolean;
}

export interface ChatRequest {
  message: string;
  userId?: string;
  documentIds?: string[];
  conversationId?: string;
}

export interface ChatMessage {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
  userId: string;
  role: string;
}

export interface FileUploadResponse {
  success: boolean;
  documentId?: string;
  errorMessage?: string;
  fileName?: string;
  fileSize?: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

export interface ConversationSession {
  id: string;
  title: string;
  lastMessageTime: string;
  messageCount: number;
  lastMessage: string;
}

export interface ChatSession {
  id: string;
  title: string;
  messages: Message[];
  timestamp: Date;
  messageCount: number;
  userId?: string;
  createdAt?: Date;
  lastMessageAt?: Date;
}

export interface User {
  id: string;
  name: string;
  email: string;
  avatar?: string;
  preferences?: UserPreferences;
}

export interface UserPreferences {
  theme: 'light' | 'dark';
  language: string;
  notifications: boolean;
}

// Runtime validators used at the localStorage boundary so corrupted or
// schema-drifted payloads fail closed instead of crashing the app on mount.
// Keep these intentionally permissive — only fields we actively read are
// required; anything else is allowed through.

export interface StoredChatSession {
  id: string;
  title: string;
  timestamp: string | Date;
  messageCount: number;
}

export function isStoredChatSessionArray(value: unknown): value is StoredChatSession[] {
  if (!Array.isArray(value)) return false;
  return value.every((item) =>
    item !== null &&
    typeof item === 'object' &&
    typeof (item as any).id === 'string' &&
    typeof (item as any).title === 'string' &&
    ((item as any).timestamp instanceof Date ||
      typeof (item as any).timestamp === 'string' ||
      typeof (item as any).timestamp === 'number') &&
    typeof (item as any).messageCount === 'number'
  );
}

export interface StoredMessage {
  id: string;
  content: string;
  isUser?: boolean;
  sender?: 'user' | 'ai';
  timestamp: string | Date;
  citations?: Citation[];
  groundingSource?: string;
  threadSummaries?: ThreadSummaryEntry[];
  suggestedApp?: SuggestedApp | null;
}

export function isStoredMessageArray(value: unknown): value is StoredMessage[] {
  if (!Array.isArray(value)) return false;
  return value.every((item) =>
    item !== null &&
    typeof item === 'object' &&
    typeof (item as any).id === 'string' &&
    typeof (item as any).content === 'string' &&
    ((item as any).timestamp instanceof Date ||
      typeof (item as any).timestamp === 'string' ||
      typeof (item as any).timestamp === 'number')
  );
}
